import React, { FC } from 'react';

import { GiftItemInfoType } from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { formatPrice } from '@shared/utils/formatPrice';
import { giftDiscountRate } from '@shared/utils/giftDiscountRate.util';

interface IProps {
  image: string;
  name: string;
  priceRegular: number;
  priceRetail: number;
  type: GiftItemInfoType;
  tags?: string[];
  isSelectable?: boolean;
  choiceUnitCount?: number;
  brandName?: string;
  //* BEST 상품(원상품 `베스트` 태그) — 이미지 위 배지로 강조
  isBest?: boolean;
}

//* 이미지 우상단 할인율 뱃지는 이 % 초과일 때만 — 1~2% 할인을 크게 띄우면 오히려 민망하다는 피드백.
//  가격 줄의 할인율·정가 취소선과 상세 페이지 표기는 그대로 둔다(할인한 건 사실이므로)
const DISCOUNT_BADGE_MIN_RATE = 2;

const GiftItemPreview: FC<IProps> = ({
  image,
  name,
  priceRegular,
  priceRetail,
  type,
  tags,
  isSelectable,
  choiceUnitCount,
  brandName,
  isBest,
}: IProps) => {
  const discountRate = giftDiscountRate(priceRegular, priceRetail);

  return (
    <div className="relative flex h-full flex-col overflow-hidden rounded-xl text-base leading-5 shadow-md ring-1 ring-black/5 theme-bg-1">
      {/* 상품 사진 */}
      <div className="relative">
        <img
          src={image}
          alt={name}
          className="aspect-square w-full object-cover object-center"
        />
        {/* 뱃지 영역 (일반 배송형은 섹션으로 이미 구분돼 이미지만 가려서 생략) */}
        {(isSelectable || type !== GiftItemInfoType.Delivery) && (
          <div className="absolute left-0 top-0 p-1.5">
            <span
              className={`inline-block rounded-sm px-1.5 py-0.5 text-[11px] font-bold text-white shadow-sm ${
                isSelectable
                  ? 'bg-gradient-to-r from-indigo-500 to-purple-500 ring-1 ring-indigo-400/50'
                  : 'bg-purple-500 ring-1 ring-purple-400/50'
              }`}
            >
              {isSelectable
                ? `선택형 ${type === GiftItemInfoType.Coupon ? '쿠폰' : '배송'}`
                : '쿠폰'}
            </span>
          </div>
        )}
        {/* BEST 뱃지 (좌하단 — 좌상단 유형 뱃지·우상단 할인율과 겹치지 않게).
            색은 상조 테마색 — 위쪽 하위분류 활성 칩과 같은 색이라 카드에 새 색이 더 얹히지 않는다 */}
        {isBest && (
          <div className="absolute bottom-0 left-0 p-1.5">
            <span className="inline-block rounded-sm px-1.5 py-0.5 text-[11px] font-extrabold tracking-wide text-white shadow-sm theme-bg-main">
              BEST
            </span>
          </div>
        )}
        {/* 할인율 뱃지 */}
        {discountRate > DISCOUNT_BADGE_MIN_RATE && (
          <div className="absolute right-1.5 top-1.5 flex aspect-square min-w-[44px] flex-col p-1 items-center justify-center rounded-full bg-red-500 text-white shadow-sm ring-1 ring-red-400/50">
            <span className="text-[13px] font-extrabold leading-none">
              {discountRate}%
            </span>
            <span className="mt-0.5 text-[9px] font-bold leading-none">
              할인
            </span>
          </div>
        )}
      </div>
      {/* 상품 정보 */}
      <div className="flex flex-1 flex-col px-2.5 pb-2.5 pt-2">
        {/* 브랜드 */}
        {brandName && (
          <span className="mb-0.5 text-[11px] font-medium text-orange-600">
            {brandName}
          </span>
        )}
        {/* 상품명 */}
        <h4 className="line-clamp-2 break-keep text-sm font-bold leading-tight">
          {name}
        </h4>
        {/* 가격 */}
        <div className="mt-1.5 flex items-baseline gap-1">
          {discountRate > 0 && (
            <span className="text-sm font-extrabold text-red-500">{discountRate}%</span>
          )}
          <span className="text-[15px] font-extrabold">{formatPrice(priceRetail)}</span>
          <span className="text-[11px] text-gray-500">원</span>
          {priceRetail !== priceRegular && priceRegular > 0 && (
            <span className="text-[10px] text-gray-400 line-through">
              {formatPrice(priceRegular)}원
            </span>
          )}
        </div>
        {/* 태그 */}
        {tags && tags.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {tags.map((tag, i) => (
              <span
                key={`${tag}-${i}`}
                className="rounded-full bg-teal-500 px-2 py-0.5 text-[11px] font-bold text-white ring-1 ring-teal-400/50"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(GiftItemPreview);
